import { AuthApiError } from './errors';

/** Which projects a person sees: every one ('all'), or only their teams' ('team', the Delivery - External role). */
export type ProjectAccess = 'all' | 'team';

/** What the UI offers someone: 'team' for the Delivery - External role, else everything. */
export function accessOf(user: Pick<CurrentUser, 'role'>): ProjectAccess {
  return user.role === 'delivery_external' ? 'team' : 'all';
}

/** What GET /api/me says about the signed-in person. */
export interface CurrentUser {
  email: string;
  name: string;
  /** Their role key (admin, delivery_external). */
  role?: string;
  /** Let in from the server's break-glass list rather than by an active user row. */
  breakGlass?: boolean;
  /** Which identity this tab is (`self` or `view:<session>`); sent back on every request (authClient.ts). */
  context?: string;
  /** Set while an admin is logged in as this person (Log in as user): who, and when it ends (ISO). */
  impersonatedBy?: { email: string; name: string; expiresAt: string };
}

/**
 * Bearer tokens must only ever be sent to this application's own API, so
 * anything that resolves to another origin, or outside /api/, is refused.
 */
export function sameOriginApiUrl(input: string, origin: string): URL {
  const apiUrl = new URL(input, origin);
  if (apiUrl.origin !== origin || !apiUrl.pathname.startsWith('/api/')) {
    throw new AuthApiError(400, 'Authenticated requests must target the Ops API.');
  }
  return apiUrl;
}

/** The AuthApiError for a failed response, with the server's message and refused email when it sent them. */
function apiError(response: Response, payload: unknown, fallbackMessage: string): AuthApiError {
  const message =
    isRecord(payload) && typeof payload.error === 'string' && payload.error.length > 0
      ? payload.error
      : fallbackMessage;
  const email = isRecord(payload) && typeof payload.email === 'string' ? payload.email : undefined;
  const reason = isRecord(payload) && typeof payload.reason === 'string' ? payload.reason : undefined;
  return new AuthApiError(response.status, message, email, reason);
}

/** Turns the GET /api/me response into the user, or an AuthApiError carrying its status. */
export async function parseMeResponse(response: Response): Promise<CurrentUser> {
  const payload = await responsePayload(response);

  if (!response.ok) {
    throw apiError(response, payload, 'Unable to check your access.');
  }

  if (!isCurrentUser(payload)) {
    throw new AuthApiError(response.status, 'The profile response was invalid.');
  }
  const by = payload.impersonatedBy;
  return {
    email: payload.email,
    name: payload.name,
    ...(typeof payload.role === 'string' ? { role: payload.role } : {}),
    ...(payload.breakGlass === true ? { breakGlass: true } : {}),
    ...(typeof payload.context === 'string' ? { context: payload.context } : {}),
    ...(isImpersonator(by) ? { impersonatedBy: { email: by.email, name: by.name, expiresAt: by.expiresAt } } : {}),
  };
}

function isImpersonator(value: unknown): value is { email: string; name: string; expiresAt: string } {
  return isRecord(value) && typeof value.email === 'string' && typeof value.name === 'string' && typeof value.expiresAt === 'string';
}

async function responsePayload(response: Response): Promise<unknown> {
  try {
    return await response.json();
  } catch {
    return undefined;
  }
}

function isCurrentUser(value: unknown): value is CurrentUser & Record<string, unknown> {
  return isRecord(value) && typeof value.email === 'string' && typeof value.name === 'string';
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}
